import { defineStore } from 'pinia';
import { get, flatMap, groupBy, reduce, keyBy } from 'lodash';
import menuLevel from '@enums/reporting-data-level';
// TODO: aov3-8283 fix circular dependency issue
// eslint-disable-next-line import/no-cycle
import { useWorkpackagesStore } from './workpackages';
import { useScenariosStore } from './scenarios';
import { useAssortmentCanvasStore } from './assortment-canvas';

export const useWorkpackageScenarioSelectStore = defineStore('workpackageScenarioSelect', {
  state: () => ({
    selectedBundles: [],
    selectedWorkpackages: [],
    scenarios: [],
    finalScenarioIds: [],
  }),

  getters: {
    selectedWorkpackageIds() {
      const workpackageStore = useWorkpackagesStore();
      const workpackages = [
        ...this.selectedWorkpackages,
        ...flatMap(this.selectedBundles, b =>
          get(workpackageStore.workpackagesByBundle, b._id, [])
        ),
      ];

      return workpackages.map(wp => wp._id);
    },

    parentHierachyIdsByScenarioId() {
      const workpackageStore = useWorkpackagesStore();
      return reduce(
        this.scenarios,
        (acc, s) => {
          const workpackageId = s.workpackageId;
          acc[s._id] = {
            bundleId: get(workpackageStore.workpackagesById[workpackageId], 'bundleId'),
            workpackageId,
          };
          return acc;
        },
        {}
      );
    },

    scenariosById() {
      return keyBy(this.scenarios, '_id');
    },

    scenariosByWorkpackage() {
      const workpackageStore = useWorkpackagesStore();
      const groupedScenarios = groupBy(this.scenarios, 'workpackageId');

      // Combine both active and archived workpackages for lookup
      const allWorkpackages = [
        ...workpackageStore.workpackages,
        ...workpackageStore.archivedWorkpackages,
        ...workpackageStore.additionalDownloadedWorkpackages,
      ];

      return allWorkpackages.reduce(
        (acc, { _id }) => ({
          ...acc,
          [_id]: groupedScenarios[_id],
        }),
        {}
      );
    },
  },

  actions: {
    setSelectedBundles(bundles) {
      this.selectedBundles = bundles;
    },

    setSelectedWorkpackages(workpackages) {
      this.selectedWorkpackages = workpackages;
    },

    setScenarios(scenarios) {
      this.scenarios = scenarios;
    },

    async fetchWorkpackageScenarios({ wpId, shouldGetFinalOnly } = {}) {
      // if workpackage id is provided, force load for that id,
      // otherwise load for selected workpackages from state
      const workpackageIds = wpId ? [wpId] : this.selectedWorkpackageIds;

      const options = { skipCommit: true };
      const scenarioStore = useScenariosStore();
      const assortmentCanvasStore = useAssortmentCanvasStore();
      this.finalScenarioIds = await assortmentCanvasStore.fetchFinalScenarios(workpackageIds, {
        skipCommit: true,
      });

      const params = {
        where: {
          workpackageId: { $in: workpackageIds },
          ...(shouldGetFinalOnly && { _id: { $in: this.finalScenarioIds || [] } }),
        },
        pick: ['workpackageId', 'name', 'customAttributes', 'creationDate', 'createdBy'],
        sortField: null,
        sortDirection: null,
      };

      const response = await scenarioStore.fetchScenarios({ params, options });
      const scenarios = response.map(s => ({
        ...s,
        menuLevel: menuLevel.scenario,
        bundleId: get(this.parentHierachyIdsByScenarioId, [s._id, 'bundleId'], null),
      }));
      this.scenarios = scenarios;

      return scenarios;
    },
  },
});
